import type { Identity } from '@rummy/protocol';
import { LOCALES, useI18n } from '../i18n/index.tsx';
import {
  EXPERIENCE_PRESETS,
  experienceOf,
  type Experience,
  type Settings,
} from '../hooks/useSettings.ts';
import { Portrait } from './Portrait.tsx';

const EXPERIENCES: Experience[] = ['learning', 'regular', 'expert'];

export interface SettingsPanelProps {
  settings: Settings;
  onChange: (patch: Partial<Settings>) => void;
  onClose: () => void;
  identity?: Identity | null;
  onSignOut?: (() => void) | undefined;
}

export function SettingsPanel({ settings, onChange, onClose, identity, onSignOut }: SettingsPanelProps) {
  const { t, locale, setLocale } = useI18n();
  const experience = experienceOf(settings);

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label={t('settings.heading')}>
      <div className="modal__panel">
        <h2>{t('settings.heading')}</h2>

        {identity && (
          <div className="settings__account">
            <Portrait character={identity.avatar} handle={identity.handle} size={44} />
            <div className="settings__who">
              <span className="settings__handle">{identity.handle}</span>
              <span className="settings__email">
                {identity.email ?? t('settings.guest')}
              </span>
            </div>
            {onSignOut && (
              <button type="button" className="btn btn--small" onClick={onSignOut}>
                {t('settings.signout')}
              </button>
            )}
          </div>
        )}

        {/*
          The panel is three questions, in the order people ask them: how do I
          want to play, how should it look, what do I need to see it. Before
          this it was sixteen switches in one list, three of which were the
          same question asked three ways.
        */}
        <h3 className="panel__section">{t('settings.section.play')}</h3>

        <div className="chips">
          {EXPERIENCES.map((option) => (
            <button
              key={option}
              type="button"
              className={`chip${experience === option ? ' is-on' : ''}`}
              aria-pressed={experience === option}
              onClick={() => onChange(EXPERIENCE_PRESETS[option])}
            >
              {t(`settings.experience.${option}`)}
            </button>
          ))}
          {experience === null && (
            <span className="chip chip--static">{t('settings.experience.custom')}</span>
          )}
        </div>
        <p className="settings__note">{t('settings.experience.help')}</p>

        <label className="check">
          <input
            type="checkbox"
            checked={settings.confirmDiscard}
            onChange={(e) => onChange({ confirmDiscard: e.target.checked })}
          />
          <span>{t('settings.confirmDiscard')}</span>
        </label>

        <label className="check">
          <input
            type="checkbox"
            checked={settings.quickPlay}
            onChange={(e) => onChange({ quickPlay: e.target.checked })}
          />
          <span>{t('settings.quickPlay')}</span>
        </label>

        <label className="check">
          <input
            type="checkbox"
            checked={settings.expertMode}
            onChange={(e) => onChange({ expertMode: e.target.checked })}
          />
          <span>{t('settings.expertMode')}</span>
        </label>

        <label className="check">
          <input
            type="checkbox"
            checked={settings.showTableIntel}
            onChange={(e) => onChange({ showTableIntel: e.target.checked })}
          />
          <span>{t('settings.tableIntel')}</span>
        </label>

        <label className="check">
          <input
            type="checkbox"
            checked={settings.showGameplayGuidance}
            onChange={(e) => onChange({ showGameplayGuidance: e.target.checked })}
          />
          <span>{t('settings.gameplayGuidance')}</span>
        </label>

        <label className="check">
          <input
            type="checkbox"
            checked={settings.turnAlert}
            onChange={(e) => onChange({ turnAlert: e.target.checked })}
          />
          <span>{t('settings.turnAlert')}</span>
        </label>

        <label className="check">
          <input
            type="checkbox"
            checked={settings.sound}
            onChange={(e) => onChange({ sound: e.target.checked })}
          />
          <span>{t('settings.sound')}</span>
        </label>

        {typeof Notification !== 'undefined' && (
          <label className="check">
            <input
              type="checkbox"
              checked={settings.turnNotifications && Notification.permission === 'granted'}
              onChange={(e) => {
                if (!e.target.checked) {
                  onChange({ turnNotifications: false });
                  return;
                }
                // The setting only takes effect once permission is actually
                // granted — otherwise a re-opened panel would show it "on"
                // while nothing ever fires.
                void Notification.requestPermission().then((permission) => {
                  onChange({ turnNotifications: permission === 'granted' });
                });
              }}
            />
            <span>{t('settings.turnNotifications')}</span>
          </label>
        )}

        <h3 className="panel__section">{t('settings.section.look')}</h3>

        <h3 className="panel__subtitle">{t('settings.theme')}</h3>
        <div className="chips">
          {(['system', 'light', 'dark'] as const).map((theme) => (
            <button
              key={theme}
              type="button"
              className={`chip${settings.theme === theme ? ' is-on' : ''}`}
              aria-pressed={settings.theme === theme}
              onClick={() => onChange({ theme })}
            >
              {t(`settings.theme.${theme}`)}
            </button>
          ))}
        </div>

        <h3 className="panel__subtitle">{t('settings.deck')}</h3>
        <div className="chips">
          <button
            type="button"
            className={`chip${settings.deck === 'two' ? ' is-on' : ''}`}
            aria-pressed={settings.deck === 'two'}
            onClick={() => onChange({ deck: 'two' })}
          >
            {t('settings.deck.two')}
          </button>
          <button
            type="button"
            className={`chip${settings.deck === 'four' ? ' is-on' : ''}`}
            aria-pressed={settings.deck === 'four'}
            onClick={() => onChange({ deck: 'four' })}
          >
            {t('settings.deck.four')}
          </button>
        </div>

        <h3 className="panel__subtitle">{t('settings.tableSkin')}</h3>
        <div className="table-skins">
          {(['jade', 'midnight', 'noir', 'sunset', 'seasonal'] as const).map((skin) => (
            <button
              key={skin}
              type="button"
              className={`table-skin table-skin--${skin}${settings.tableSkin === skin ? ' is-on' : ''}`}
              aria-pressed={settings.tableSkin === skin}
              onClick={() => onChange({ tableSkin: skin })}
            >
              <span className="table-skin__swatch" aria-hidden="true" />
              <span>{t(`settings.tableSkin.${skin}`)}</span>
            </button>
          ))}
        </div>

        <h3 className="panel__subtitle">{t('settings.cardBack')}</h3>
        <div className="card-backs">
          {(['club', 'aurora', 'ruby', 'ivory'] as const).map((back) => (
            <button
              key={back}
              type="button"
              className={`card-back-choice card-back-choice--${back}${settings.cardBack === back ? ' is-on' : ''}`}
              aria-pressed={settings.cardBack === back}
              onClick={() => onChange({ cardBack: back })}
            >
              <span className="card-back-choice__sample" aria-hidden="true">✦</span>
              <span>{t(`settings.cardBack.${back}`)}</span>
            </button>
          ))}
        </div>

        <h3 className="panel__subtitle">{t('settings.playerAccent')}</h3>
        <div className="player-accents">
          {(['violet', 'rose', 'aqua', 'gold'] as const).map((accent) => (
            <button
              key={accent}
              type="button"
              className={`player-accent player-accent--${accent}${settings.playerAccent === accent ? ' is-on' : ''}`}
              aria-pressed={settings.playerAccent === accent}
              onClick={() => onChange({ playerAccent: accent })}
            >
              <span aria-hidden="true" />
              {t(`settings.playerAccent.${accent}`)}
            </button>
          ))}
        </div>

        <h3 className="panel__section">{t('settings.section.access')}</h3>

        <h3 className="panel__subtitle">{t('settings.accessibility')}</h3>
        <div className="chips">
          {(['compact', 'comfortable', 'large'] as const).map((cardSize) => (
            <button
              key={cardSize}
              type="button"
              className={`chip${settings.cardSize === cardSize ? ' is-on' : ''}`}
              aria-pressed={settings.cardSize === cardSize}
              onClick={() => onChange({ cardSize })}
            >
              {t(`settings.cardSize.${cardSize}`)}
            </button>
          ))}
        </div>

        <div className="chips settings__handedness">
          {(['right', 'left'] as const).map((handedness) => (
            <button
              key={handedness}
              type="button"
              className={`chip${settings.handedness === handedness ? ' is-on' : ''}`}
              aria-pressed={settings.handedness === handedness}
              onClick={() => onChange({ handedness })}
            >
              {t(`settings.handedness.${handedness}`)}
            </button>
          ))}
        </div>

        <label className="check">
          <input
            type="checkbox"
            checked={settings.highContrast}
            onChange={(e) => onChange({ highContrast: e.target.checked })}
          />
          <span>{t('settings.highContrast')}</span>
        </label>

        {/* A budget, not a switch: "less" and "none" are different requests. */}
        <h3 className="panel__subtitle">{t('settings.motion')}</h3>
        <div className="chips">
          {(['full', 'reduced', 'none'] as const).map((motion) => (
            <button
              key={motion}
              type="button"
              className={`chip${settings.motion === motion ? ' is-on' : ''}`}
              aria-pressed={settings.motion === motion}
              onClick={() => onChange({ motion })}
            >
              {t(`settings.motion.${motion}`)}
            </button>
          ))}
        </div>
        <p className="settings__note">{t('settings.motion.help')}</p>

        <h3 className="panel__subtitle">{t('settings.language')}</h3>
        <div className="chips">
          {LOCALES.map((option) => (
            <button
              key={option.code}
              type="button"
              className={`chip${locale === option.code ? ' is-on' : ''}`}
              aria-pressed={locale === option.code}
              onClick={() => setLocale(option.code)}
              lang={option.code}
            >
              {option.native}
              {option.status === 'in-review' && <span className="chip__note">·</span>}
            </button>
          ))}
        </div>

        <div className="modal__actions">
          <button type="button" className="btn btn--primary" onClick={onClose}>
            {t('settings.close')}
          </button>
        </div>
      </div>
    </div>
  );
}
